/**
 * 出金申請操作
 *
 * 出金申請フローを制御するMixin。
 * 金額入力、確認画面表示、送信を担当。
 * キャンセル確認ダイアログは PayoutRequestCancelHandlingMixin で管理。
 *
 * 操作するDOM要素:
 *   payoutRequestInputTarget                  - 金額入力フィールド
 *   payoutRequestPlaceholderTarget            - 入力プレースホルダー
 *   payoutRequestNoteTarget                   - 注意書き表示
 *   payoutRequestErrorTarget                  - エラーメッセージ表示
 *   payoutRequestHistoryTabContainerTarget    - 履歴タブコンテナ
 *   payoutRequestExecutionContainerTarget     - 申請実行ボタンコンテナ
 *   confirmAmountDisplayTarget                - 確認画面の金額表示
 *
 * 使用するStimulus values:
 *   payoutRequestAmountValue           - ユーザー入力の出金希望額
 *   minimumPayoutAmountValue           - 出金最低金額
 *   availableBalanceValue              - 出金可能残高（Turbo Streamで更新）
 *
 * 依存するメソッド（他のMixinから）:
 *   reloadHarvestHistoryFrame()        - 収穫残高履歴フレームリロード（TurboHelpersMixin）
 *   updateFooterDialogueText()         - 会話テキスト更新（FooterDialogueManagementMixin）
 */
import axios from "@/modules/axios/index";
import { Turbo } from "@hotwired/turbo-rails";
import { NotificationTypes } from "../constants";

/**
 * 出金申請処理
 *
 * 出金申請フォームの入力検証、送信、キャンセル処理を担当。
 * 送信/キャンセル成功時はTurbo Streamで残高表示を更新。
 */
export const PayoutRequestHandlingMixin = {
  // ============================================
  // 初期化
  // ============================================

  /**
   * 出金申請画面の初期状態を設定
   * switchToScenePayoutRequestTop() から呼ばれる
   */
  initializePayoutRequestState() {
    if (this.hasPayoutRequestInputTarget) {
      const inputValue = this.payoutRequestInputTarget.value;

      // Toggle placeholder visibility
      if (this.hasPayoutRequestPlaceholderTarget) {
        this.payoutRequestPlaceholderTarget.classList.toggle("hidden", inputValue.length > 0);
      }

      this.setButtonContainersViewState(inputValue.length > 0 ? "request" : "history");
    }
  },

  // ============================================
  // 入力フェーズ（_top.html.erb）
  // ============================================

  /**
   * 金額入力フィールドのキー入力制限
   * 数値以外の文字（-、e、.、+）をブロック
   * トリガー: 金額入力フィールドのkeydownイベント
   * HTML: payout_request/_top.html.erb
   *       data-action="keydown->harvest-warehouse#onKeydownAmount ..."
   */
  onKeydownAmount(event) {
    if (["-", "e", "E", ".", "+"].includes(event.key)) {
      event.preventDefault();
    }
  },

  /**
   * 金額入力時の処理
   * トリガー: 金額入力フィールドのinputイベント
   * HTML: payout_request/_top.html.erb
   *       data-action="... input->harvest-warehouse#onChangeAmount"
   */
  onChangeAmount(event) {
    const input = event.target;

    // Sanitize: remove non-numeric characters and leading zeros
    const sanitized = input.value.replace(/[^0-9]/g, "").replace(/^0+(?=\d)/, "");
    if (input.value !== sanitized) {
      input.value = sanitized;
    }

    const hasContent = sanitized.length > 0;

    // Toggle placeholder visibility
    if (this.hasPayoutRequestPlaceholderTarget) {
      this.payoutRequestPlaceholderTarget.classList.toggle("hidden", hasContent);
    }

    // 入力有無に応じて履歴タブ/申請ボタンを切り替え
    this.setButtonContainersViewState(hasContent ? "request" : "history");

    // Store the numeric value
    this.payoutRequestAmountValue = parseInt(sanitized, 10) || 0;
  },

  /**
   * 金額入力フィールドからフォーカスが外れた時の処理
   * フッター会話テキストを更新（入力中ではなく、入力完了後に切り替え）
   * トリガー: 金額入力フィールドのblurイベント
   * HTML: payout_request/_top.html.erb
   *       data-action="... blur->harvest-warehouse#onBlurAmount"
   */
  onBlurAmount() {
    if (!this.payoutInputDialogueShown) {
      this.updateFooterDialogueText();
    }
  },

  // ============================================
  // ヘルパー（入力フェーズ用）
  // ============================================

  /**
   * 出金依頼画面のボタン表示を切り替え
   * @param {string} view - "request"（申請ボタン表示）または "history"（履歴タブ表示）
   */
  setButtonContainersViewState(view) {
    const config = {
      request: { history: "none", request: "flex" },
      history: { history: "flex", request: "none" },
    };
    const display = config[view];

    if (this.hasPayoutRequestHistoryTabContainerTarget) {
      this.payoutRequestHistoryTabContainerTarget.style.display = display.history;
    }

    if (this.hasPayoutRequestExecutionContainerTarget) {
      this.payoutRequestExecutionContainerTarget.style.display = display.request;
    }
  },

  // ============================================
  // 申請フェーズ（_top.html.erb → _confirm.html.erb）
  // ============================================

  /**
   * 出金申請ボタン押下時の処理
   * 金額バリデーション後、確認画面へ遷移またはエラー表示
   * トリガー: 申請ボタンのclickイベント
   * HTML: payout_request/_top.html.erb
   *       data-action="click->harvest-warehouse#applyPayoutRequest"
   */
  applyPayoutRequest() {
    const amount = this.payoutRequestAmountValue;

    if (amount < this.minimumPayoutAmountValue) {
      this.showPayoutError("min_error");
      this.updateFooterDialogueText(NotificationTypes.PAYOUT_REQUEST_TOP_MIN_ERROR);
    } else if (amount > this.availableBalanceValue) {
      this.showPayoutError("max_error");
      this.updateFooterDialogueText(NotificationTypes.PAYOUT_REQUEST_TOP_MAX_ERROR);
    } else {
      // Populate confirm screen data
      this.updatePayoutConfirmScreen();
      // Switch to confirm scene
      this.switchToScenePayoutRequestConfirm();
    }
  },

  // ============================================
  // ヘルパー（申請フェーズ用）
  // ============================================

  /**
   * 確認画面に金額データを設定
   */
  updatePayoutConfirmScreen() {
    const amount = this.payoutRequestAmountValue;
    const formattedAmount = this.formatNumberWithDelimiters(amount);

    if (this.hasConfirmAmountDisplayTarget) {
      this.confirmAmountDisplayTarget.textContent = formattedAmount;
    }
  },

  /**
   * 通常の注意書きを表示
   */
  showPayoutNote() {
    if (this.hasPayoutRequestNoteTarget) {
      this.payoutRequestNoteTarget.style.display = "block";
    }
    if (this.hasPayoutRequestErrorTarget) {
      this.payoutRequestErrorTarget.style.display = "none";
    }
  },

  /**
   * エラーメッセージを表示
   * @param {string} type - "min_error"（最低金額エラー）または "max_error"（残高超過エラー）
   */
  showPayoutError(type) {
    const errorMessages = {
      min_error: `${this.formatAmountWithCurrency(this.minimumPayoutAmountValue)}以上で出金申請してください`,
      max_error: "出金希望金額が出金可能金額を超えています",
    };

    if (this.hasPayoutRequestNoteTarget) {
      this.payoutRequestNoteTarget.style.display = "none";
    }

    if (this.hasPayoutRequestErrorTarget) {
      this.payoutRequestErrorTarget.style.display = "block";
      this.payoutRequestErrorTarget.textContent = errorMessages[type];
    }
  },

  // ============================================
  // 送信フェーズ（_confirm.html.erb）
  // NOTE: 残高・自動出金の表示更新はTurbo Streamが担当。
  //       base branchのJSON応答 + updateBalanceDisplays/updateAutoPayoutDisplays は
  //       Turbo Stream導入により不要となった。
  //       詳細は docs/harvest-warehouse/controller.md を参照。
  // ============================================

  /**
   * 出金申請送信処理（AJAX）
   * トリガー: 確認画面の申請確定ボタンclickイベント
   * HTML: payout_request/_confirm.html.erb
   *       data-action="click->harvest-warehouse#submitPayoutRequest"
   */
  async submitPayoutRequest(event) {
    const button = event.currentTarget;
    button.disabled = true;

    const afterSuccess = (response) => {
      Turbo.renderStreamMessage(response.data);

      // Reset payout request scene
      this.resetPayoutRequestState();

      this.reloadPayoutRequestHistoryFrame();
      this.reloadHarvestHistoryFrame();

      // Navigate to TOP and show success notification
      this.switchToSceneTop({ notification: NotificationTypes.PAYOUT_REQUEST_CONFIRM_SUBMIT_SUCCESS });
    };

    const catchError = (error) => {
      const errorMessage = error.response?.data?.message || "出金申請に失敗しました";
      console.error("Payout request failed:", errorMessage);

      // Return to payout request top and show error in footer
      this.switchToScenePayoutRequestTop({ notification: NotificationTypes.PAYOUT_REQUEST_CONFIRM_SUBMIT_ERROR });
    };

    await axios
      .post(
        this.submitPayoutRequestPathValue,
        { amount: this.payoutRequestAmountValue },
        { headers: { Accept: "text/vnd.turbo-stream.html" } },
      )
      .then(afterSuccess)
      .catch(catchError)
      .finally(() => {
        button.disabled = false;
      });
  },

  // ============================================
  // キャンセル・リセット
  // ============================================

  /**
   * 出金申請画面からキャンセル
   * TOP画面へ戻る
   */
  cancelPayoutRequest() {
    this.switchToSceneTop();
  },

  /**
   * 確認画面からキャンセル
   * 出金申請画面へ戻る（入力値は保持）
   */
  cancelPayoutRequestConfirm() {
    this.switchToScenePayoutRequestTop();
  },

  // ============================================
  // ヘルパー（リセット処理）
  // NOTE: scene-navigation.jsへの移動を検討
  //       現在switchToSceneTop()から呼ばれており、
  //       シーン遷移に関連する処理のため、
  //       scene-navigation.jsに含める方が適切かもしれない
  // ============================================

  /**
   * 出金申請画面を初期状態にリセット
   * 入力値クリア、プレースホルダー表示、メッセージリセット
   */
  resetPayoutRequestState() {
    // Clear input
    if (this.hasPayoutRequestInputTarget) {
      this.payoutRequestInputTarget.value = "";
    }

    // Show placeholder
    if (this.hasPayoutRequestPlaceholderTarget) {
      this.payoutRequestPlaceholderTarget.classList.remove("hidden");
    }

    // 通常の注意書きを表示
    this.showPayoutNote();

    // 履歴タブを表示、申請ボタンを非表示にリセット
    this.setButtonContainersViewState("history");

    // Reset amount value
    this.payoutRequestAmountValue = 0;
  },
};
